import React from 'react';
import { 
  Package, AlertTriangle, Clock, Edit, 
  MoreVertical, Eye, Layers, Trash2
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

const ProductCardView = ({ 
  items, 
  onItemClick, 
  onEditItem, 
  onDeleteItem,
  onAdjustStock 
}) => {
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  // Get expiry status
  const getExpiryStatus = (item) => {
    if (!item.expiry_date) return null;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const expiryDate = new Date(item.expiry_date);
    const daysUntilExpiry = Math.ceil((expiryDate - today) / (1000 * 60 * 60 * 24));
    
    if (daysUntilExpiry < 0) {
      return { status: 'expired', label: `${Math.abs(daysUntilExpiry)}d ago`, color: 'red', days: Math.abs(daysUntilExpiry) };
    } else if (daysUntilExpiry <= 7) {
      return { status: 'expiring-critical', label: `${daysUntilExpiry}d left`, color: 'red', days: daysUntilExpiry };
    } else if (daysUntilExpiry <= 30) {
      return { status: 'expiring', label: `${daysUntilExpiry}d left`, color: 'orange', days: daysUntilExpiry };
    }
    return null;
  };

  // Get stock status
  const getStockStatus = (item) => {
    if (item.item_type === 'service') return null;
    const stock = item.current_stock || 0;
    const reorderLevel = item.reorder_level || item.stock_config?.low_stock_threshold || 10;
    
    if (stock === 0) {
      return { status: 'out', label: 'Out of Stock', color: 'red' };
    } else if (stock <= reorderLevel) {
      return { status: 'low', label: 'Low Stock', color: 'orange' };
    }
    return null;
  };

  if (items.length === 0) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center py-12">
        <div className="w-12 h-12 bg-slate-100 rounded-lg flex items-center justify-center mb-3">
          <Package className="w-6 h-6 text-slate-400" />
        </div>
        <p className="text-sm text-slate-600">No products found</p>
        <p className="text-xs text-slate-400 mt-0.5">Try adjusting your filters</p>
      </div>
    );
  }

  return (
    <TooltipProvider delayDuration={200}>
      {/* 4 Column Grid on Desktop */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2.5 p-3">
        {items.map((item, index) => {
          const expiryStatus = getExpiryStatus(item);
          const stockStatus = getStockStatus(item);
          const primaryImage = item.primary_image_url || (item.images?.[0]?.url);
          const hasRedWarning = expiryStatus?.color === 'red' || stockStatus?.color === 'red';
          const hasOrangeWarning = !hasRedWarning && (expiryStatus?.color === 'orange' || stockStatus?.color === 'orange');
          
          return (
            <div
              key={item.id}
              data-testid={`product-card-${item.id}`}
              onClick={() => onItemClick?.(item)}
              className={`
                group relative bg-white rounded-lg border overflow-hidden cursor-pointer
                transition-all duration-150 ease-out
                hover:shadow-sm hover:border-slate-300
                ${hasRedWarning ? 'border-red-200 bg-red-50/30' : 
                  hasOrangeWarning ? 'border-orange-200 bg-orange-50/20' : 
                  'border-slate-200'}
              `}
              style={{
                animationDelay: `${index * 20}ms`,
                animation: 'cardIn 0.2s ease-out forwards',
                opacity: 0
              }}
            >
              {/* Compact Card Content */}
              <div className="p-3">
                <div className="flex gap-3">
                  {/* Product Image - Larger for better visibility */}
                  <div className={`
                    w-16 h-16 rounded-lg flex-shrink-0 flex items-center justify-center overflow-hidden
                    ${primaryImage ? 'bg-slate-50' : 'bg-gradient-to-br from-teal-50 to-slate-100'}
                    border border-slate-200 shadow-sm
                  `}>
                    {primaryImage ? (
                      <img 
                        src={primaryImage} 
                        alt={item.name}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.parentElement.innerHTML = '<svg class="w-6 h-6 text-teal-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"></path></svg>';
                        }}
                      />
                    ) : (
                      <Package className="w-6 h-6 text-teal-400" />
                    )}
                  </div>

                  {/* Product Info */}
                  <div className="flex-1 min-w-0">
                    {/* Name */}
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <h3 className="text-sm font-medium text-slate-800 truncate leading-tight">
                          {item.name}
                        </h3>
                      </TooltipTrigger>
                      <TooltipContent side="top" className="max-w-[200px]">
                        <p className="text-[11px]">{item.name}</p>
                      </TooltipContent>
                    </Tooltip>
                    
                    {/* SKU/Code */}
                    <p className="text-[11px] text-slate-400 truncate font-mono mt-0.5">
                      {item.code || item.sku || 'No SKU'}
                    </p>

                    {/* Meta chips row */}
                    <div className="flex items-center gap-1 mt-1 flex-wrap">
                      {/* Category */}
                      {item.category && (
                        <span className="inline-flex px-1 py-0.5 rounded text-[8px] font-medium bg-slate-100 text-slate-500 leading-none">
                          {item.category}
                        </span>
                      )}
                      {/* Unit */}
                      <span className="text-[9px] text-slate-400">
                        {item.unit || item.primary_unit || 'PCS'}
                      </span>
                    </div>
                  </div>

                  {/* 3-dot Menu */}
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                      <button className="p-1 text-slate-300 hover:text-slate-500 hover:bg-slate-100 rounded transition-colors opacity-0 group-hover:opacity-100 -mr-1 -mt-0.5">
                        <MoreVertical className="w-3.5 h-3.5" />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-32" onClick={(e) => e.stopPropagation()}>
                      <DropdownMenuItem onClick={() => onItemClick?.(item)} className="text-[11px] py-1.5">
                        <Eye className="w-3 h-3 mr-1.5" />
                        View Details
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => onEditItem?.(item)} className="text-[11px] py-1.5">
                        <Edit className="w-3 h-3 mr-1.5" />
                        Edit
                      </DropdownMenuItem>
                      {item.item_type === 'product' && (
                        <DropdownMenuItem onClick={() => onAdjustStock?.(item)} className="text-[11px] py-1.5">
                          <Layers className="w-3 h-3 mr-1.5" />
                          Adjust Stock
                        </DropdownMenuItem>
                      )}
                      <DropdownMenuSeparator className="my-1" />
                      <DropdownMenuItem 
                        onClick={() => onDeleteItem?.(item)}
                        className="text-[11px] py-1.5 text-red-600 focus:text-red-600 focus:bg-red-50"
                      >
                        <Trash2 className="w-3 h-3 mr-1.5" />
                        Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>

                {/* Status Badges - Below content */}
                {(expiryStatus || stockStatus || item.item_type === 'service') && (
                  <div className="flex items-center gap-1 mt-2 flex-wrap">
                    {/* Expired Badge */}
                    {expiryStatus?.status === 'expired' && (
                      <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[9px] font-medium bg-red-500 text-white leading-none">
                        <Clock className="w-2.5 h-2.5" />
                        Expired {expiryStatus.label}
                      </span>
                    )}
                    {/* Expiring Critical (7d) */}
                    {expiryStatus?.status === 'expiring-critical' && (
                      <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[9px] font-medium bg-red-100 text-red-700 leading-none">
                        <Clock className="w-2.5 h-2.5" />
                        Exp {expiryStatus.label}
                      </span>
                    )}
                    {/* Expiring Soon (30d) */}
                    {expiryStatus?.status === 'expiring' && (
                      <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[9px] font-medium bg-orange-100 text-orange-700 leading-none">
                        <Clock className="w-2.5 h-2.5" />
                        Exp {expiryStatus.label}
                      </span>
                    )}
                    
                    {/* Out of Stock */}
                    {stockStatus?.status === 'out' && (
                      <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-medium bg-red-500 text-white leading-none">
                        Out of Stock
                      </span>
                    )}
                    {/* Low Stock */}
                    {stockStatus?.status === 'low' && (
                      <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-medium bg-orange-100 text-orange-700 leading-none">
                        Low Stock
                      </span>
                    )}
                    
                    {/* Service Badge */}
                    {item.item_type === 'service' && (
                      <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-medium bg-violet-100 text-violet-600 leading-none">
                        Service
                      </span>
                    )}
                  </div>
                )}

                {/* Price & Stock Row */}
                <div className="flex items-end justify-between mt-2 pt-2 border-t border-slate-100">
                  <div>
                    <p className="text-sm font-medium text-teal-600">
                      {formatCurrency(item.selling_price || item.pricing?.sales_price)}
                    </p>
                    {(item.purchase_price || item.pricing?.purchase_price) > 0 && (
                      <p className="text-[9px] text-slate-400 mt-0.5">
                        Cost: {formatCurrency(item.purchase_price || item.pricing?.purchase_price)}
                      </p>
                    )}
                  </div>
                  
                  {item.item_type === 'product' && (
                    <div className="text-right">
                      <p className={`text-sm font-medium ${
                        stockStatus?.color === 'red' ? 'text-red-600' :
                        stockStatus?.color === 'orange' ? 'text-orange-600' :
                        'text-slate-700'
                      }`}>
                        {item.current_stock || 0}
                        <span className="text-[9px] font-normal text-slate-400 ml-0.5">
                          {item.unit || item.primary_unit || 'PCS'}
                        </span>
                      </p>
                      {/* Stock warning text */}
                      {stockStatus?.color === 'red' && (
                        <p className="text-[9px] text-red-500 mt-0.5">No stock</p>
                      )}
                      {stockStatus?.color === 'orange' && (
                        <p className="text-[9px] text-orange-500 mt-0.5">Reorder needed</p>
                      )}
                    </div>
                  )}
                </div>

                {/* Expiry Date Text (if expired or expiring) */}
                {item.expiry_date && expiryStatus && (
                  <p className={`text-[9px] mt-1.5 ${
                    expiryStatus.color === 'red' ? 'text-red-500' : 'text-orange-500'
                  }`}>
                    {expiryStatus.status === 'expired' 
                      ? `Expired: ${new Date(item.expiry_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: '2-digit' })}`
                      : `Expires: ${new Date(item.expiry_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: '2-digit' })}`
                    }
                  </p>
                )}
              </div>
            </div>
          );
        })}

        {/* CSS Animation */}
        <style>{`
          @keyframes cardIn {
            from {
              opacity: 0;
              transform: translateY(4px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
        `}</style>
      </div>
    </TooltipProvider>
  );
};

export default ProductCardView;
